import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ErrorIcon from '@mui/icons-material/Error';

import DeleteIconBordered from '../../../../../../../components/svgs/DeleteIconBordered';
import { FHButton } from '../../../../../../../components/base';
import { FAutocomplete } from '../../../../../../../components/baseV2';
import { FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../../../../../../utils/colors';
import { Themes } from '../../../../../../../constants';
import { ConfirmDialog, LIST_ROW_BORDER_SX, useFormReadOnly } from '../../../../../../common';
import { REFERRAL_LIST_ROW_BG_SX } from '../styles';

import type { FAutocompleteOption } from '../../../../../../../components/baseV2';
import type { ReferralListFieldProps } from './types';

export default function ReferralListField({
  label,
  addButtonLabel,
  placeholder,
  options,
  values,
  disabled = false,
  required = false,
  error = false,
  helperText,
  confirmDeleteTitle,
  requiredIndicatorSx,
  onChange,
}: ReferralListFieldProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // A read-only questionnaire locks the whole field — no adding or deleting — while the
  // selected values stay visible. This layers on top of any transient `disabled`.
  const readOnly = useFormReadOnly();
  const [pendingValue, setPendingValue] = useState<FAutocompleteOption | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FAutocompleteOption | null>(null);

  useEffect(() => {
    if (readOnly) {
      setPendingDelete(null);
    }
  }, [readOnly]);

  const optionsByValue = useMemo(() => {
    const map = new Map<string, FAutocompleteOption>();
    options.forEach((option) => map.set(option.value, option));
    return map;
  }, [options]);

  const availableOptions = useMemo(
    () => options.filter((option) => !values.includes(option.value)),
    [options, values],
  );

  const getOptionLabel = useCallback((option: FAutocompleteOption) => option.label, []);
  const isOptionEqualToValue = useCallback(
    (option: FAutocompleteOption, selected: FAutocompleteOption) => option.value === selected.value,
    [],
  );

  const handleAdd = useCallback(() => {
    if (!pendingValue) {
      return;
    }
    onChange([...values, pendingValue.value]);
    setPendingValue(null);
  }, [onChange, pendingValue, values]);

  const handleRequestDelete = useCallback((option: FAutocompleteOption) => {
    setPendingDelete(option);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (readOnly || pendingDelete == null) {
      return;
    }
    setPendingDelete(null);
    onChange(values.filter((value) => value !== pendingDelete.value));
  }, [onChange, pendingDelete, readOnly, values]);

  const addButtonStack = (
    <Stack direction="row" justifyContent="flex-end" alignItems="center">
      <FHButton
        colorTheme={Themes.Primary}
        disabled={disabled || !pendingValue}
        onClick={handleAdd}
        startIcon={
          error ? (
            <ErrorIcon sx={{ color: FORTA_ERROR }} />
          ) : (
            <AddIcon sx={{ color: FORTA_WHITE }} />
          )
        }
      >
        {addButtonLabel}
      </FHButton>
    </Stack>
  );

  return (
    <Box>
      {!readOnly && (
        <>
          {!isMobile && <Box mb={2}>{addButtonStack}</Box>}

          <FAutocomplete
            disabled={disabled || availableOptions.length === 0}
            fullWidth
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            label={label}
            onChange={(_event, option) => setPendingValue(option)}
            options={availableOptions}
            placeholder={placeholder}
            required={required}
            requiredIndicatorSx={requiredIndicatorSx}
            value={pendingValue}
          />

          {isMobile && <Box mt={2}>{addButtonStack}</Box>}
        </>
      )}

      {values.length > 0 ? (
        <Stack mt={2}>
          {values.map((value, index) => {
            const optionLabel = optionsByValue.get(value)?.label ?? value;
            return (
              <Stack
                key={`${value}-${index}`}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={[
                  { px: 2, py: 1, ...LIST_ROW_BORDER_SX },
                  REFERRAL_LIST_ROW_BG_SX[index % 2],
                ]}
              >
                <Typography sx={{ color: FORTA_BLUE.dark }}>{optionLabel}</Typography>
                {!readOnly && (
                  <IconButton
                    aria-label={`Delete ${optionLabel}`}
                    disabled={disabled}
                    onClick={() => handleRequestDelete({ value, label: optionLabel })}
                    size="small"
                    sx={{ p: 0 }}
                  >
                    <DeleteIconBordered width={36} height={36} />
                  </IconButton>
                )}
              </Stack>
            );
          })}
        </Stack>
      ) : null}

      {helperText ? <FormHelperText error>{helperText}</FormHelperText> : null}

      <ConfirmDialog
        open={pendingDelete != null}
        title={confirmDeleteTitle}
        message={pendingDelete ? `You are about to delete "${pendingDelete.label}"` : ''}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
}
